/**
 * How much longer a chunked recording has to go.
 *
 * "Part 29 of 39" does not answer the question anyone actually has, which is
 * whether to wait or go and make coffee. Worse, it stands still: a part is an
 * upload of a few seconds followed by tens of seconds of recognition, so for
 * most of the run nothing on screen moves. A number that counts down every
 * few seconds fixes both — it answers the real question AND it is proof the
 * job is alive.
 *
 * The speed is measured, never assumed. Each finished part contributes how
 * many seconds of audio it carried and how long it took, and the rate is the
 * two totals divided — which is what makes the last part (routinely a third
 * the length of the others) come out right instead of being counted as a
 * whole one.
 */

/** One finished part: what it carried, and what it cost. */
export interface EtaSample {
  /** Seconds of recording in this part */
  audioSeconds: number
  /** Wall-clock seconds it took, upload and recognition together */
  elapsedSeconds: number
}

/**
 * Two parts before an estimate is offered.
 *
 * One point is not a speed — it is a single measurement of a gateway that may
 * have been having a slow minute, and an estimate that later triples is worse
 * than no estimate at all. So the first two parts run with a caption and no
 * number, and the figure appears while the third is in flight.
 */
export const MIN_SAMPLES = 2

/**
 * Seconds still to wait, or **null** when there is not enough to say.
 *
 * `audioSecondsLeft` counts from the start of the part in hand, and
 * `elapsedInCurrent` is how long that part has already been running — which
 * is what makes the answer fall between parts instead of sitting still until
 * one finishes.
 */
export function estimateRemaining(
  samples: EtaSample[],
  audioSecondsLeft: number,
  elapsedInCurrent: number,
): number | null {
  if (samples.length < MIN_SAMPLES) return null
  let audio = 0
  let elapsed = 0
  for (const s of samples) {
    audio += s.audioSeconds
    elapsed += s.elapsedSeconds
  }
  if (audio <= 0 || elapsed <= 0) return null
  const rate = audio / elapsed // seconds of recording handled per second
  return Math.max(0, audioSecondsLeft / rate - elapsedInCurrent)
}

/**
 * What the recognition is expected to manage while we wait, in seconds of
 * recording per second — used to move the bar, never to print a number.
 *
 * Before two parts have finished there is nothing to measure and the bar
 * still has to move, so it starts on a guess. The guess used to be 13 — the
 * figure this repo carries for a free gateway — and on the dev stand the
 * first part took over 190 seconds for 12 minutes of audio, under 4. The bar
 * reached the end of its segment in 81 seconds and sat there for the rest,
 * which is the frozen screen this whole thing exists to remove. So the guess
 * is conservative now, and more to the point the curve below no longer
 * depends on it being right.
 */
export const ASSUMED_RATE = 3

export function processingRate(samples: EtaSample[]): number {
  if (samples.length < MIN_SAMPLES) return ASSUMED_RATE
  let audio = 0
  let elapsed = 0
  for (const s of samples) {
    audio += s.audioSeconds
    elapsed += s.elapsedSeconds
  }
  if (audio <= 0 || elapsed <= 0) return ASSUMED_RATE
  return audio / elapsed
}

/** The furthest into its own segment the bar may go. A bar sitting at 100 %
 * is the worst reading of all — worse than one that has honestly stopped,
 * because it claims the work is done. */
export const SEGMENT_CEILING = 0.95

/** How far the bar tracks the estimate literally before it starts easing.
 * Below this the estimate is being believed; above it, doubted. */
const LINEAR_UNTIL = 0.7

/**
 * How far into its own part the clock has carried us, 0–`SEGMENT_CEILING`.
 *
 * The clock is measured against an ESTIMATE, and the estimate can be badly
 * wrong (it was, by 3× on the first part of a real run). Something that runs
 * out its part and waits is the frozen screen again, so past `LINEAR_UNTIL`
 * it eases toward the ceiling instead of hitting it: always moving, never
 * arriving, however wrong the estimate turns out to be. While the estimate
 * holds — which is from the third part on, when it is measured rather than
 * assumed — the movement is plain and linear.
 */
function withinPart(elapsedInCurrent: number, expectedPartSeconds: number): number {
  if (!(expectedPartSeconds > 0)) return 0
  const t = Math.max(0, elapsedInCurrent) / expectedPartSeconds
  return t < LINEAR_UNTIL
    ? t
    : SEGMENT_CEILING - (SEGMENT_CEILING - LINEAR_UNTIL) * Math.exp(-(t - LINEAR_UNTIL))
}

/**
 * **Seconds of the recording** behind us — the one number the bar and the
 * number beside it are both drawn from.
 *
 * It is seconds, not a fraction of the parts, and that distinction is the
 * whole point. The bar is labelled with durations at both ends, so it claims
 * to be the recording; a fill measured in parts is not, because the last
 * part is routinely a third the length of the others. Counting parts put the
 * fill at 4/5 = 80 % where the recording was really 90 % through, and the
 * caption underneath — which was reading the true offset — disagreed with
 * the fill sitting directly above it.
 *
 * Whole minutes of this are honest ones plus an eased guess at the part in
 * hand, and the guess never crosses into the next part, so the number can
 * lead the truth by less than one part and never by more.
 */
export function audioDone(
  offsetSeconds: number,
  partAudioSeconds: number,
  elapsedInCurrent: number,
  expectedPartSeconds: number,
): number {
  return offsetSeconds + partAudioSeconds * withinPart(elapsedInCurrent, expectedPartSeconds)
}
